(() => {
  'use strict';
  if (window.tnmFreeMeatGift) return;

  const root = window.Shopify?.routes?.root || '/';
  const giftId = 46397096394914; // Published $0 Hawaiian Kalua Pork variant.
  const threshold = 15000;
  const marker = '_tnm_free_meat_150';
  const message = 'We could not confirm your free pack. Please review your cart and try checkout again.';
  let pending = null;
  let runs = 0;
  let lastResult = null;
  let replay = false;
  let busy = false;
  const replayForms = new WeakSet();
  const caseFormsBusy = new WeakSet();

  const notice = document.createElement('div');
  notice.setAttribute('role', 'alert');
  notice.setAttribute('tabindex', '-1');
  notice.hidden = true;
  notice.style.cssText = 'position:fixed;z-index:2147483001;bottom:1rem;left:1rem;right:1rem;max-width:38rem;margin:auto;padding:1rem 1.25rem;background:#fff7e6;color:#26190f;border:2px solid #a04414;border-radius:.5rem;box-shadow:0 4px 24px #0004;font:400 1rem/1.5 system-ui,sans-serif';
  notice.textContent = message + ' ';
  const cartLink = document.createElement('a');
  cartLink.href = root + 'cart';
  cartLink.textContent = 'Review your cart';
  cartLink.style.cssText = 'color:#71310d;font-weight:700;text-decoration:underline';
  notice.appendChild(cartLink);
  document.body.appendChild(notice);

  const request = async (path, body) => {
    const response = await fetch(root + path, {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {'Accept': 'application/json', ...(body ? {'Content-Type': 'application/json'} : {})},
      ...(body ? {body: JSON.stringify(body)} : {}),
    });
    if (!response.ok) throw new Error('Cart request failed');
    const data = await response.json();
    if (!data || data.errors) throw new Error('Cart response failed');
    return data;
  };
  const read = async () => {
    const cart = await request('cart.js');
    if (!Array.isArray(cart.items)) throw new Error('Cart unavailable');
    return cart;
  };
  // A seed is a full cart body from the caller's own latest verified read or
  // cart write. It must look like a read, or the cart is read instead.
  const usable = (cart) => Boolean(cart && typeof cart === 'object' && !cart.errors &&
    Array.isArray(cart.items) && cart.items.every((line) => line && typeof line === 'object'));
  const owned = (line) => line.properties?.[marker] === 'true';
  const builderGift = (line) => line.properties?._toro_bundle_free_gift === 'true' &&
    line.properties?._toro_bundle_gift_entitlement === 'free_meat';
  const paidMeatCents = (cart) => cart.items.reduce((sum, line) => {
    const type = String(line.product_type || '').toLowerCase();
    if (line.gift_card || type === 'gift card' || type === 'shipping' ||
      line.handle === 'shipping-insurance' || !line.requires_shipping ||
      line.final_line_price <= 0 || owned(line) || builderGift(line)) return sum;
    return sum + line.final_line_price;
  }, 0);
  const giftCount = (cart) => cart.items.reduce((sum, line) =>
    sum + (builderGift(line) && line.final_line_price === 0 ? line.quantity : 0), 0);
  const ownLines = (cart) => cart.items.filter(owned);
  const otherInventory = (cart) => JSON.stringify(cart.items.filter((line) => !owned(line)).map((line) => [
    line.variant_id, line.quantity, line.selling_plan_allocation?.selling_plan?.id || null,
    Object.entries(line.properties || {}).sort(([a], [b]) => a.localeCompare(b)),
  ]).sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b))));

  const reconcile = async (seed) => {
    let cart = usable(seed) ? seed : await read();
    const before = otherInventory(cart);
    let changed = false;
    // A bundle-builder gift IS the $150 offer for that cart (the builder sets its own
    // gift size, e.g. 1 free chicken); never top it up with Kalua packs.
    const target = giftCount(cart) > 0 ? 0 : (paidMeatCents(cart) >= threshold ? 1 : 0);
    // Re-read after every write because Shopify can rotate other line keys.
    for (let writes = 0; writes < 6; writes += 1) {
      const lines = ownLines(cart);
      const invalid = lines.find((line) => Number(line.variant_id) !== giftId || line.final_line_price !== 0);
      const extra = lines.length > 1 ? lines[1] : null;
      const first = lines[0];
      const line = invalid || extra || (first && first.quantity !== target ? first : null);
      if (!line) break;
      if (!line.key) throw new Error('Gift line has no key');
      const quantity = line === first && !invalid && !extra ? target : 0;
      await request('cart/change.js', {id: line.key, quantity});
      changed = true;
      cart = await read();
    }
    const present = ownLines(cart).reduce((sum, line) => sum + line.quantity, 0);
    if (present < target) {
      await request('cart/add.js', {items: [{id: giftId, quantity: target - present, properties: {[marker]: 'true'}}]});
      changed = true;
      cart = await read();
    }
    const current = ownLines(cart);
    if (current.reduce((sum, line) => sum + line.quantity, 0) !== target ||
      current.some((line) => Number(line.variant_id) !== giftId || line.final_line_price !== 0) ||
      otherInventory(cart) !== before ||
      (target > 0 && paidMeatCents(cart) < threshold)) throw new Error('Free packs not verified');
    return {safe: true, cart, changed};
  };
  const ensure = (seed) => {
    if (!pending) {
      runs += 1;
      pending = reconcile(seed).catch(() => ({safe: false}))
        .then((result) => { lastResult = result; return result; })
        .finally(() => {pending = null;});
    }
    return pending;
  };
  window.tnmFreeMeatGift = {ensure};

  const checkoutUrl = (value) => {
    try { const url = new URL(value, location.href); return url.origin === location.origin && /\/checkouts?(?:\/|$)/.test(url.pathname); }
    catch { return false; }
  };
  const show = () => { notice.hidden = false; notice.focus({preventScroll: true}); };
  const caseDealCheckout = async (form) => {
    if (caseFormsBusy.has(form)) return;
    caseFormsBusy.add(form);
    const button = form.querySelector('button[type="submit"]');
    if (button) button.disabled = true;
    try {
      const id = Number(form.querySelector('[name="id"]')?.value);
      const quantity = Number(form.querySelector('[name="quantity"]')?.value);
      const price = Number(form.closest('[data-case-card]')?.dataset.price);
      if (!Number.isSafeInteger(id) || id <= 0 || !Number.isSafeInteger(quantity) || quantity <= 0 ||
        !Number.isSafeInteger(price) || price <= 0) throw new Error('Invalid case');
      form.dataset.tnmCaseAttempt ||= window.crypto?.randomUUID?.() || String(Date.now());
      const attempt = form.dataset.tnmCaseAttempt;
      const before = await read();
      const existing = before.items.find((line) => line.properties?._tnm_case_attempt === attempt);
      const preserved = (cart) => otherInventory({items: cart.items.filter((line) => line.properties?._tnm_case_attempt !== attempt)});
      const snapshot = preserved(before);
      if (!existing) {
        try {
          await request('cart/add.js', {items: [{id, quantity, properties: {_tnm_case_attempt: attempt}}]});
        } catch {
          // Read back before retrying: an accepted add may lose its response.
        }
      }
      const after = await read();
      const added = after.items.filter((line) => line.properties?._tnm_case_attempt === attempt);
      if (added.length !== 1 || Number(added[0].variant_id) !== id || added[0].quantity !== quantity ||
        !Number.isSafeInteger(added[0].final_price) || added[0].final_price > price ||
        preserved(after) !== snapshot) throw new Error('Case not verified');
      const halloween = window.tnmHalloweenGiftGuard;
      if (halloween && !(await halloween.ensureSafe()).safe) throw new Error('Cart gift not verified');
      if (!(await ensure()).safe) throw new Error('Free packs not verified');
      location.assign(root + 'checkout');
    } catch {show();}
    finally {
      caseFormsBusy.delete(form);
      if (button) button.disabled = false;
    }
  };
  const gate = async (event, resume) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (busy) return;
    busy = true;
    try {
      const halloween = window.tnmHalloweenGiftGuard;
      if (halloween && !(await halloween.ensureSafe()).safe) {show(); return;}
      if (!(await ensure()).safe) {show(); return;}
      notice.hidden = true;
      resume();
    } catch {show();}
    finally {busy = false;}
  };
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || replayForms.has(form)) return;
    if (form.matches('.case-deal__form')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      void caseDealCheckout(form);
      return;
    }
    const submitter = event.submitter;
    if (!checkoutUrl(submitter?.getAttribute('formaction') || form.action) &&
      !(new URL(form.action, location.href).pathname.replace(/\/$/, '') === root.replace(/\/$/, '') + '/cart' &&
        (submitter?.name === 'checkout' || (!submitter && form.querySelector('[name="checkout"]'))))) return;
    gate(event, () => {
      replayForms.add(form);
      form.requestSubmit(submitter || undefined);
      setTimeout(() => replayForms.delete(form), 10000);
    });
  }, true);
  document.addEventListener('click', (event) => {
    if (replay) return;
    const target = event.target?.closest?.('a[href], button[name="checkout"], input[name="checkout"]');
    if (!target || target.closest('[data-hw-buy]')) return;
    if (target.matches('a[href]') && checkoutUrl(target.href)) {
      gate(event, () => location.assign(target.href));
    } else if (target.form && target.name === 'checkout') {
      gate(event, () => {
        replayForms.add(target.form);
        replay = true;
        try { target.form.requestSubmit(target); } finally { replay = false; }
        setTimeout(() => replayForms.delete(target.form), 10000);
      });
    }
  }, true);
  // The builder's checkout handshake. detail.cart is the cart its final write
  // returned, so the guards can start from it instead of reading again.
  document.addEventListener('torosachi:cart-safety', (event) => {
    if (typeof event.detail?.waitUntil !== 'function') return;
    const seed = usable(event.detail.cart) ? event.detail.cart : undefined;
    // A run already going when the handshake starts may predate the builder's
    // write: wait for it, then read fresh (the stale-cart class, PR #380).
    const stale = pending;
    const runsBefore = runs;
    event.detail.waitUntil((async () => {
      const halloween = window.tnmHalloweenGiftGuard;
      let cart = seed;
      if (halloween) {
        const guarded = halloween.ensureForHandshake
          ? await halloween.ensureForHandshake(seed) : await halloween.ensureSafe();
        if (!guarded.safe) return {safe: false};
        cart = halloween.ensureForHandshake ? guarded.cart : undefined;
      }
      if (stale) { await stale; return ensure(); }
      // The Halloween check ends in case shipping, which runs this guard last;
      // that run started after the handshake and is the newest cart state.
      if (runs !== runsBefore) {
        const latest = pending ? await pending : lastResult;
        if (latest?.safe) return latest;
        return ensure();
      }
      return ensure(cart);
    })());
  });
  if (location.pathname.replace(/\/$/, '') === root.replace(/\/$/, '') + '/cart') {
    void (async () => {
      const halloween = window.tnmHalloweenGiftGuard;
      if (halloween && !(await halloween.ensureSafe()).safe) return;
      const result = await ensure();
      if (result.safe && result.changed) location.reload();
    })();
  }
})();
